import { AfterViewInit, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, map, Observable, Subscription, switchMap } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { includes } from 'underscore';
import { difference } from 'lodash';
import moment from 'moment-timezone';

import { TableColumnModel } from '../../../../Common/Forms/models/table-column.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';
import { CommonRowFields } from '../../../../Common/Tags/components/ManageTagsDialog/manage-tags-dialog.component';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TableColumnInputEnum } from '../../../../Common/Forms/enums/table-column-input-type.enum';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AgentService } from '../../services/agent.service';
import { Theme } from '../../models/theme.model';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { ThemeService } from '../../services/theme.service';
import { AutocompleteComponent } from '../../../../Common/Forms/components/Autocomplete/autocomplete.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { AddAgentsToThemeDialogComponent } from '../AddAgentsToThemeDialog/add-agents-to-theme-dialog.component';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { AgentWidgetComponent, AgentWidgetMode } from '../AgentWidget/agent-widget.component';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { NetworkTypeEnum, mediaTypeToNetwork } from '../../../Core/enums/network-type.enum';
import { mediaSubTypeToNetworkTypeMap } from '../../../../Social/Intelligence/models/network-type-to-media-sub-type.map';
import { MediaSubscriptionApiService } from '../../../../api/mediaSubscriptions/media-subscription.api.service';
import { MediaSubscriptionBackfill } from '../../../../Monitor/MediaSubscriptions/models/media-subscription-backfill.model';
import { BackfillStatusApiEnum } from '../../../../api/mediaSubscriptions/enums/backfill-status.api.enum';
import { ConfirmDialogAlertMessage } from '../../../../Common/Dialogs/models/confirm-dialog-alert-message.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AgentSourceIndexService } from '../../../../AutomationAgents/services/agent-source-index.service';
import { ManageSourceAgentsService } from '../../../../AutomationAgents/services/manage-source-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';


interface SocialTopicDataRow extends CommonRowFields {
    originalData: MediaSubscription;
    status: MediaSubscriptionStatusEnum;
    themeListChips?: ChipModel[];
    themeList?: Theme[];
    // selected theme from autocomplete to add social topic to
    selectedTheme?: Theme;
    // autocomplete dropdown options
    suggestions?: Observable<Theme[]>;
    isPartiallyModerated?: boolean;
    partiallyModeratedNetworks?: string;
    agentNetworks?: NetworkTypeEnum[];
}

@Component({
    selector: 'agpr-manage-social-topics-dialog',
    templateUrl: './manage-social-topics-dialog.component.html',
    styleUrls: ['./manage-social-topics-dialog.component.scss'],
    standalone: false
})
export class ManageSocialTopicsDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() id: string = 'agentManageDialog';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() theme: Theme;

    @ViewChild('nameCell') nameCell: TemplateRef<any>;
    @ViewChild('statusCell') statusCell: TemplateRef<any>;
    @ViewChild('themeListCell') themeListCell: TemplateRef<any>;
    @ViewChild('actionsCell') actionsCell: TemplateRef<any>;
    @ViewChild('themeAutocomplete') autocomplete: AutocompleteComponent;

    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly nameColumn: TableColumnModel = {
        heading: 'Social.manageTopicsDialog.socialTopicName',
        field: 'name',
        sortable: true,
        sortType: 'text',
        type: TableColumnInputEnum.custom,
        cellStyleClass: 'agpr-manageSocialTopicsDialog-topicName',
        width: 270
    };
    readonly statusColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        cellStyleClass: 'agpr-manageSocialTopicsDialog-topicStatus',
        heading: 'Social.manageTopicsDialog.status',
        field: 'status',
        sortable: true,
        sortType: 'text',
        width: 150
    };
    readonly themeListColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Social.manageTopicsDialog.inThemes',
        field: 'themeListChips',
    };
    /** Last column, and only for accounts that can have automation agents */
    readonly actionsColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Social.manageTopicsDialog.actions',
        field: 'actions',
        cellStyleClass: 'agpr-manageSocialTopicsDialog-topicActions',
        width: 90
    };

    columnDefs: TableColumnModel[] = [];
    dataRows: SocialTopicDataRow[] = [];
    allSocialTopics: MediaSubscription[] = [];
    selectedTopics: SocialTopicDataRow[] = [];
    socialTopicToThemesMap: Map<number, Theme[]>;

    currentTheme: Theme;
    loading: boolean = true;

    /** Whether the account can have automation agents, which the last row option is offered for */
    hasAgenticFeature: boolean = false;

    /** The row's menu - the row it acts on arrives on the command as event.data */
    readonly rowOptions: MenuItem[] = [
        {
            label: 'Social.manageTopicsDialog.editTopic',
            command: (event: MenuItemCommandEvent) => this.openAgentWidget(event['data'] as SocialTopicDataRow)
        },
        {
            label: 'Social.manageTopicsDialog.deleteTopic',
            command: (event: MenuItemCommandEvent) => this.deleteTopic(event['data'] as SocialTopicDataRow)
        },
        {
            label: 'AutomationAgents.source.addLiveAgent',
            icon: 'fa-kit-duotone fa-agpr-ai-generic',
            visible: false,
            command: (event: MenuItemCommandEvent) => this.addLiveAgent(event['data'] as SocialTopicDataRow)
        }
    ];

    /** Automation agents live on each social topic, keyed by AgentSourceIndexService.key */
    private liveAgentCounts: Map<string, number> = new Map<string, number>();
    dateFormat: string;

    private allowedNetworks: NetworkTypeEnum[] = [];
    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private usersService: UsersService,
        private agentService: AgentService,
        private themeService: ThemeService,
        private localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        private monitorConfigService: MonitorConfigService,
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private agentSourceIndexService: AgentSourceIndexService,
        private manageSourceAgentsService: ManageSourceAgentsService
    ) {
        super(coreDialogService, 'ManageSocialTopicsDialog');
    }

    /** How many automation agents are live on a topic, for the marker beside its name */
    liveAgentCount(row: SocialTopicDataRow): number {
        return this.liveAgentCounts.get(
            this.agentSourceIndexService.key(MonitoringSeriesTypeEnum.socialTopic, row?.originalData?.id)
        ) ?? 0;
    }

    liveAgentTooltip(row: SocialTopicDataRow): string {
        return this.localizationService.transform('AutomationAgents.liveAgent', { count: `${this.liveAgentCount(row)}` });
    }

    /** Opens the panel that manages the automation agents watching this topic */
    addLiveAgent(row: SocialTopicDataRow): void {
        this.manageSourceAgentsService.open(MonitoringSeriesTypeEnum.socialTopic, row?.originalData?.id);
    }

    ngOnInit() {
        this.subscriptions.push(
            // Topics an automation agent is watching, marked as they are on the theme cards
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => this.liveAgentCounts = counts),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => {
                this.hasAgenticFeature = enabled;
                // Only accounts that can have agents are offered the last one
                this.rowOptions[this.rowOptions.length - 1].visible = this.hasAgenticFeature;
            }),
            combineLatest([
                this.agentService.all$,
                this.monitorConfigService.getAvailableSocialMediaTypes()
            ]).subscribe(([agents, mediaTypes]) => {

                if (!agents || !mediaTypes) {
                    return;
                }

                this.loading = false;

                this.allSocialTopics = agents;
                this.allowedNetworks = mediaTypes
                    .map(media => mediaTypeToNetwork.get(media))
                    .filter(x => !!x);

                if (this.allowedNetworks.includes(NetworkTypeEnum.reddit)) {
                    this.allowedNetworks.push(NetworkTypeEnum.subReddit);
                }
                this.setData();
            }),
            this.themeService.agentThemeMap$.subscribe(socialTopicToThemes => {
                this.socialTopicToThemesMap = socialTopicToThemes;
                if (this.allSocialTopics) {
                    this.setData();
                }
            }),
            this.themeService.currentResource$.pipe(filter(x => !!x)).subscribe(
                currentTheme => {
                    this.currentTheme = currentTheme;
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true);
                this.setData();
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.nameColumn.customTemplate = this.nameCell;
            this.statusColumn.customTemplate = this.statusCell;
            this.themeListColumn.customTemplate = this.themeListCell;
            this.actionsColumn.customTemplate = this.actionsCell;
            this.columnDefs = [
                {
                    type: TableColumnInputEnum.checkbox,
                    field: 'selected',
                    cellStyleClass: 'agpr-manageSocialTopicsDialog-topicCheckbox',
                    canSelectAll: true,
                    width: 57,
                },
                this.nameColumn,
                this.statusColumn,
                this.themeListColumn,
                this.actionsColumn
            ];
        });
    }

    private setData() {
        if (!this.dateFormat) {
            return;
        }
        const userIds = [];
        // Set source row data
        this.dataRows = this.allSocialTopics.map((topic) => {
            const row: SocialTopicDataRow = {
                originalData: topic,
                name: topic.name,
                createdDate: moment(topic.createdDate).format(this.dateFormat),
                createdByUserId: topic.createdByUserId,
                themeListChips: [],
                themeList: [],
                status: topic.status,
                selectedTheme: null,
                isPartiallyModerated:topic.isPartiallyModerated,
                partiallyModeratedNetworks: topic.partiallyModeratedNetworks
            };
            userIds.push(topic.createdByUserId);
            if (topic.modifiedByUserId) {
                row.lastModifiedByUserId = topic.modifiedByUserId;
                userIds.push(topic.modifiedByUserId);
                row.lastModifiedDate = moment(topic.modifiedDate).format(this.dateFormat);
            };
            if (this.socialTopicToThemesMap) {
                const themeList = this.socialTopicToThemesMap.get(topic.id);
                if (themeList) {
                    themeList.forEach(theme => {
                        row.themeListChips.push({
                            label: theme.name
                        });
                        row.themeList.push(theme);
                    });
                    row.suggestions = this.getThemeSuggestions(row);
                } else {
                    row.suggestions = this.themeService.all$.pipe(
                        map(themes => themes.filter(theme => theme.subscriptionIds.length < Theme.MAX_NUM_AGENTS)));
                }
            }

            if (this.allowedNetworks.length > 0) {
                row.agentNetworks = topic.mediaTypeRequests
                    .map(x => mediaSubTypeToNetworkTypeMap.get(x.mediaType))
                    .filter(network => this.allowedNetworks.includes(network));
            }

            return row;
        });
        this.checkForSelected();
        this.fetchUserNames(userIds);
    }

    rowChanged(topicRow: SocialTopicDataRow) {
        if (topicRow.status !== topicRow.originalData.status) {
            const agent = topicRow.originalData;
            agent.status = topicRow.status;

            this.loading = true;

            this.agentService.update(agent).pipe(
                finalize(() => this.loading = false)
            ).subscribe();
        }
    }

    checkForSelected() {
        this.selectedTopics = this.dataRows.filter(row => row.selected === true);
    }

    /**
     * For the info tooltip, convert user ids to usernames
     */
    private fetchUserNames(userIds): void {
        this.usersService.getUsers(userIds).subscribe(
            (users: UserModel[]) => {
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user;
                });

                this.dataRows.forEach((row) => {
                    const createdUser = userMap[row.createdByUserId];
                    if (createdUser) {
                        row.createdByUser = createdUser.getDisplayName();
                    }
                    if (row.lastModifiedByUserId) {
                        const modifiedUser = userMap[row.lastModifiedByUserId];
                        if (modifiedUser) {
                            row.lastModifiedByUser = modifiedUser.getDisplayName();
                        }
                    }
                });
                this.dataRows = this.dataRows.slice();
            }
        );
    }

    /**
     * When removing theme that associated with an agent, we'll delete the agent id from subscriptionIds[] of the selected theme
     */
    deleteThemeChip(index: number, row: SocialTopicDataRow) {
        const selectedTheme: Theme = row.themeList[index];
        const agent: MediaSubscription = row.originalData;
        const socialTopicIdIndex = selectedTheme.subscriptionIds.indexOf(agent?.id);
        // If last theme associated is selected to remove, show warning dialog
        if (row.themeList.length === 1) {
            /* eslint-disable @stylistic/indent */
            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'social.removeThemeConfirmDialog',
                    heading: 'Social.intelligence.topicManagement.removeTheme',
                    messageHtml:
                    `<div>
                        ${this.localizationService.transform('Social.intelligence.topicManagement.removeTheme.message.1',
                            { agent: row.name, theme: selectedTheme.name }
                        )}
                    </div>
                    <br>
                    <div>
                        ${this.localizationService.transform('Social.intelligence.themeManagement.themeCard.removeAgent.message.2')}
                    </div>`,
                    okLabel: 'Social.intelligence.themeManagement.themeCard.removeAgent.okLabel',
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            selectedTheme.subscriptionIds.splice(socialTopicIdIndex, 1);

                            row.themeList.splice(index, 1);

                            this.loading = true;
                            this.themeService.update(selectedTheme).pipe(
                                switchMap(() => this.agentService.switchChange(MediaSubscriptionStatusEnum.PAUSED, agent)),
                                finalize(() => this.loading = false)
                            ).subscribe();
                        }
                    }
                }
            };
            /* eslint-enable @stylistic/indent */

            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            selectedTheme.subscriptionIds.splice(socialTopicIdIndex, 1);
            row.themeList.splice(index, 1);

            this.loading = true;
            this.themeService.update(selectedTheme).pipe(
                finalize(() => this.loading = false)
            ).subscribe();
        }

    }

    selectTheme(selectedTheme: Theme, row: SocialTopicDataRow) {
        if (!row.themeList.find(theme => theme.id === selectedTheme.id)) {
            row.themeList.push(selectedTheme);

            selectedTheme.subscriptionIds.push(row.originalData?.id);

            this.loading = true;
            this.themeService.update(selectedTheme).pipe(
                finalize(() => this.loading = false)
            ).subscribe();
        }
    }

    deleteTopic(topicRow: SocialTopicDataRow) {
        this.mediaSubscriptionApiService.getById(topicRow.originalData.id).pipe(
            tap(socialTopic => topicRow.originalData.backfills = socialTopic.backfills?.map(x => new MediaSubscriptionBackfill(x)) || []),
            finalize(() => {
                const alertMessages: ConfirmDialogAlertMessage[] = [
                    { alertMessage: 'Social.manageTopicsDialog.deleteSocialTopic.warning', alertType: messageSeverityEnum.warning }
                ];
                const hasActiveBackfills = topicRow.originalData.backfills.some(x => x.status === BackfillStatusApiEnum.inProgress);
                if (hasActiveBackfills) {
                    alertMessages.push({
                        alertMessage: 'Social.manageTopicsDialog.deleteSocialTopic.activeBackfillMessage',
                        alertType: messageSeverityEnum.warning
                    });
                }
                const io: ConfirmDialogModel = {
                    inputs: {
                        id: 'discover.deleteListConfirmDialog',
                        heading: 'Social.manageTopicsDialog.deleteSocialTopic',
                        message: 'Social.manageTopicsDialog.deleteSocialTopic.message',
                        alertMessages: alertMessages,
                        list: [topicRow.name],
                        okLabel: 'Delete'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.loading = true;

                                this.agentService.delete(topicRow.originalData).pipe(
                                    finalize(() => this.loading = false)
                                ).subscribe();
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            })
        ).subscribe();
    }

    openAddToThemeDialog() {
        this.coreDialogService.open(AddAgentsToThemeDialogComponent, {
            inputs: {
                agentsToAdd:  this.selectedTopics.map(topic => topic.originalData)
            },
            outputs: {}
        });
    }


    openAgentWidget(row?: SocialTopicDataRow) {
        if (row) {
            const themeIds = row.themeList.map(theme => theme.id);
            if (
                (themeIds.length !== row.originalData.themeIds.length)
                || (difference(themeIds, row.originalData.themeIds).length > 0)
            ) {
                row.originalData.themeIds = themeIds;
            }
        }
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const agentWidgetMode = row ? AgentWidgetMode.edit : AgentWidgetMode.create;

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                theme: this.currentTheme || this.theme,
                agentModel: row ? row.originalData : null,
                mode: agentWidgetMode
            },
            outputs: {}
        };
        this.coreDialogService.open(AgentWidgetComponent, io);
    }

    getThemeSuggestions(row: SocialTopicDataRow): Observable<Theme[]> {
        // filter out themes that are already added and over the limit
        const themeIds = row.themeList.map(theme => theme.id);
        return this.themeService.all$.pipe(map(themes => {
            return themes.filter(x => {
                return !includes(themeIds, x.id) && x.subscriptionIds.length < Theme.MAX_NUM_AGENTS;
            });
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
